import React, { useCallback, useState } from 'react';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { Box, styled } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { Button } from '@rentspree/component-2023.components.atoms.button';
import { Typography } from '@rentspree/component-2023.components.atoms.typography';

export const stepTitle = 'Add photos to your listing';
export const stepDescription =
  'Upload high-quality photos to attract more tenants.';

interface UploadedFile extends File {
  preview: string;
}

const DropzoneContainer = styled('div')(({ theme }) => ({
  border: `2px dashed ${theme.palette.grey[400]}`,
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(6),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: '#fafafa',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: '#f5f5f5',
  },
}));

const PhotoGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
  gap: 16,
  marginTop: 24,
});

const PhotoCard = styled('div')({
  position: 'relative',
  aspectRatio: '4/3',
  borderRadius: 12,
  overflow: 'hidden',
  border: '1px solid #e0e0e0',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
});

const PhotoImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

// Mock photos for demo
const mockPhotos = [
  {
    id: 1,
    url: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=300&h=225&fit=crop',
    alt: 'Living room',
  },
  {
    id: 2,
    url: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=300&h=225&fit=crop',
    alt: 'Kitchen',
  },
  {
    id: 3,
    url: 'https://images.unsplash.com/photo-1560185007-5f0bb1866cab?w=300&h=225&fit=crop',
    alt: 'Bedroom',
  },
];

const UploadPhotos: React.FC = () => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [showMockPhotos, setShowMockPhotos] = useState(true);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setShowMockPhotos(false);
    setFiles(
      acceptedFiles.map(file =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      )
    );
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': [],
    },
    multiple: true,
  });

  const displayPhotos = showMockPhotos ? mockPhotos : files;

  return (
    <Box
      sx={{
        maxWidth: 700,
        margin: '0 auto',
        padding: { xs: 2, md: 4 },
      }}
    >
      <DropzoneContainer {...getRootProps()}>
        <input {...getInputProps()} data-testid='file-input' />
        <AddPhotoAlternateIcon
          sx={{
            fontSize: 60,
            color: 'grey.500',
            mb: 2,
          }}
        />
        <Typography
          variant='h6'
          sx={{
            mb: 1,
            fontWeight: 600,
          }}
        >
          {isDragActive ? 'Drop the files here...' : 'Drag & drop photos here'}
        </Typography>
        <Typography
          sx={{
            color: 'text.secondary',
            mb: 3,
            fontSize: '0.875rem',
          }}
        >
          or click to browse files
        </Typography>
        <Button
          variant='outlined'
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1.5,
          }}
        >
          Browse Files
        </Button>
      </DropzoneContainer>

      {displayPhotos.length > 0 && (
        <>
          <Typography
            variant='h6'
            sx={{
              mt: 4,
              mb: 2,
              fontWeight: 600,
            }}
          >
            {showMockPhotos ? 'Sample Photos' : 'Uploaded Photos'} (
            {displayPhotos.length})
          </Typography>

          <PhotoGrid>
            {showMockPhotos
              ? mockPhotos.map(photo => (
                  <PhotoCard key={photo.id}>
                    <PhotoImage src={photo.url} alt={photo.alt} />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                      }}
                    >
                      {photo.id === 1 ? 'Main' : `Photo ${photo.id}`}
                    </Box>
                  </PhotoCard>
                ))
              : files.map((file, index) => (
                  <PhotoCard key={file.name}>
                    <PhotoImage
                      src={file.preview}
                      alt={`Uploaded photo ${index + 1}`}
                      onLoad={() => URL.revokeObjectURL(file.preview)}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                      }}
                    >
                      {index === 0 ? 'Main' : `Photo ${index + 1}`}
                    </Box>
                  </PhotoCard>
                ))}

            {/* Add more photos placeholder */}
            <Box
              sx={{
                aspectRatio: '4/3',
                border: '2px dashed #ccc',
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                backgroundColor: '#fafafa',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#999',
                  backgroundColor: '#f0f0f0',
                },
              }}
              onClick={() => {
                const fileInput = document.querySelector(
                  'input[type="file"]'
                ) as HTMLInputElement;
                fileInput?.click();
              }}
            >
              <AddPhotoAlternateIcon
                sx={{
                  fontSize: 32,
                  color: 'grey.400',
                  mb: 1,
                }}
              />
              <Typography
                variant='body2'
                sx={{
                  color: 'grey.600',
                  textAlign: 'center',
                  fontSize: '0.75rem',
                }}
              >
                Add more
              </Typography>
            </Box>
          </PhotoGrid>

          {showMockPhotos && (
            <Typography
              variant='body2'
              sx={{
                color: 'text.secondary',
                textAlign: 'center',
                mt: 3,
                fontStyle: 'italic',
              }}
            >
              These are sample photos. Upload your own photos to replace them.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default UploadPhotos;
